/**
 * Shell completion engine — walks the Commander program tree dynamically.
 *
 * Called via `arbi --get-completions "<line>"`. Derives all candidates from
 * the live Commander registrations, so new commands/options are automatically
 * available without maintaining a static registry.
 */

import type { Command, Option } from 'commander'
import { getCachedWorkspaceIds } from './completion-cache.js'

/** Options that take a workspace ID as their value. */
const WORKSPACE_ID_OPTIONS = new Set(['-w', '--workspace'])

/** Collect option flags (short + long) from a Command. */
function getOptionFlags(cmd: Command): string[] {
  const flags: string[] = []
  for (const opt of cmd.options as Option[]) {
    if (opt.short) flags.push(opt.short)
    if (opt.long) flags.push(opt.long)
  }
  return flags
}

/** Find the Option object that matches a given flag. */
function findOption(cmd: Command, flag: string): Option | undefined {
  return (cmd.options as Option[]).find((o) => o.short === flag || o.long === flag)
}

/**
 * Return completion candidates for the given input line.
 * Walks the Commander command tree — no static registries needed.
 */
export function getCompletions(program: Command, line: string): string[] {
  const parts = line.trim().split(/\s+/)

  // Remove the program name (e.g. "arbi" or "/usr/local/bin/arbi")
  if (parts.length > 0) {
    const first = parts[0]
    if (first === program.name() || first.endsWith('/' + program.name())) {
      parts.shift()
    }
  }

  const endsWithSpace = line.endsWith(' ')

  // Walk the command tree to find the deepest matching command
  let current: Command = program
  let consumed = 0

  for (let i = 0; i < parts.length; i++) {
    const word = parts[i]
    const sub = current.commands.find(
      (c: Command) => c.name() === word || c.aliases().includes(word)
    )
    if (sub) {
      current = sub
      consumed = i + 1
    } else {
      break
    }
  }

  const remaining = parts.slice(consumed)
  const lastWord = remaining.length > 0 ? remaining[remaining.length - 1] : ''

  // Check if the previous completed word is a workspace ID option
  const prevWord = endsWithSpace ? remaining[remaining.length - 1] : remaining[remaining.length - 2]
  if (prevWord && WORKSPACE_ID_OPTIONS.has(prevWord)) {
    return getCachedWorkspaceIds()
  }

  // Gather candidates: subcommands + option flags
  const subcommands = current.commands.map((c: Command) => c.name())
  const options = getOptionFlags(current)
  const allCandidates = [...subcommands, ...options]

  // Nothing typed yet after current command, or cursor is after a space
  if (remaining.length === 0 || (endsWithSpace && remaining.length >= 0)) {
    if (endsWithSpace) {
      // Filter out already-used options (and their short/long pairs)
      const used = new Set<string>()
      for (const part of remaining) {
        if (part.startsWith('-')) {
          used.add(part)
          const opt = findOption(current, part)
          if (opt) {
            if (opt.short) used.add(opt.short)
            if (opt.long) used.add(opt.long)
          }
        }
      }
      return allCandidates.filter((c) => !used.has(c))
    }
    return allCandidates
  }

  // Partial word being typed — filter candidates by prefix
  return allCandidates.filter((c) => c.startsWith(lastWord))
}
